import React, { useState, useEffect } from 'react';
import { 
  Tag, Users, Plus, Trash2, Edit2, Check, X, Search,
  Target, PieChart, UserPlus, Filter, Sparkles, Zap, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SegmentManager = ({ headers, fetchData }) => {
  const [segments, setSegments] = useState([]);
  const [segmentStats, setSegmentStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showSuggestionsDialog, setShowSuggestionsDialog] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [newSegment, setNewSegment] = useState({ name: '', description: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSegments();
    fetchSegmentStats();
    fetchSubscribers();
  }, []);

  const fetchSegments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/emails/segments`, { headers });
      setSegments(res.data);
    } catch (err) {
      console.error('Failed to fetch segments');
    }
  };

  const fetchSegmentStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/emails/segments/stats`, { headers });
      setSegmentStats(res.data);
    } catch (err) {
      console.error('Failed to fetch segment stats');
    }
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/newsletter/subscribers`, { headers });
      setSubscribers(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch subscribers');
      setLoading(false);
    }
  };

  const fetchSmartSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API_URL}/api/segments/suggestions/bulk-suggestions?limit=50`, { headers });
      setSuggestions(res.data.suggestions || []);
      setShowSuggestionsDialog(true);
    } catch (err) {
      console.error('Failed to fetch suggestions');
      toast.error('Failed to load smart suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const applySmartSuggestion = async (email, segmentId) => {
    try {
      await axios.post(`${API_URL}/api/emails/segments/assign`, {
        subscriber_emails: [email],
        segment_ids: [segmentId]
      }, { headers });
      toast.success(`Assigned ${email} to segment`);
      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.email !== email));
      fetchSegments();
      fetchSegmentStats();
    } catch (err) {
      toast.error('Failed to assign segment');
    }
  };

  const applyAllSuggestions = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/segments/suggestions/auto-assign`, {}, { headers });
      toast.success(res.data.message);
      setShowSuggestionsDialog(false);
      fetchSegments();
      fetchSegmentStats();
      fetchSubscribers();
    } catch (err) {
      toast.error('Failed to auto-assign segments');
    }
  };

  const createSegment = async () => {
    if (!newSegment.name.trim()) {
      toast.error('Segment name is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/emails/segments`, newSegment, { headers });
      toast.success('Segment created');
      setShowCreateDialog(false);
      setNewSegment({ name: '', description: '' });
      fetchSegments();
      fetchSegmentStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create segment');
    }
  };

  const deleteSegment = async (segmentId) => {
    if (!window.confirm('Delete this segment? Subscribers will be removed from it.')) return;
    try {
      await axios.delete(`${API_URL}/api/emails/segments/${segmentId}`, { headers });
      toast.success('Segment deleted');
      fetchSegments();
      fetchSegmentStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete segment');
    }
  };

  const assignSubscribers = async () => {
    if (!selectedSegment || selectedEmails.length === 0) return;
    try {
      await axios.post(`${API_URL}/api/emails/segments/assign`, {
        subscriber_emails: selectedEmails,
        segment_ids: [selectedSegment]
      }, { headers });
      toast.success(`Assigned ${selectedEmails.length} subscribers to segment`);
      setShowAssignDialog(false);
      setSelectedEmails([]);
      setSelectedSegment(null);
      fetchSegments();
      fetchSegmentStats();
      fetchSubscribers();
    } catch (err) {
      toast.error('Failed to assign subscribers');
    }
  };

  const removeFromSegment = async (email, segmentId) => {
    try {
      await axios.post(`${API_URL}/api/emails/segments/remove`, {
        subscriber_emails: [email],
        segment_ids: [segmentId]
      }, { headers });
      toast.success('Removed from segment');
      fetchSegments();
      fetchSegmentStats();
      fetchSubscribers();
    } catch (err) {
      toast.error('Failed to remove subscriber');
    }
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (sub.name && sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleEmail = (email) => {
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  return (
    <div className="space-y-6" data-testid="segment-manager">
      {/* Stats Overview */}
      {segmentStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Subscribers</p>
                <p className="text-2xl font-bold">{segmentStats.total_subscribers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Tag className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Segmented</p>
                <p className="text-2xl font-bold">{segmentStats.segmented_subscribers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Filter className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unsegmented</p>
                <p className="text-2xl font-bold">{segmentStats.unsegmented_subscribers}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <PieChart className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Segmentation Rate</p>
                <p className="text-2xl font-bold">{segmentStats.segmentation_rate}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Subscriber Segments</h2>
          <p className="text-sm text-gray-500">Organize subscribers into segments for targeted campaigns</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={fetchSmartSuggestions} 
            variant="outline" 
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            disabled={loadingSuggestions}
            data-testid="smart-suggestions-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" /> {loadingSuggestions ? 'Loading...' : 'Smart Suggestions'}
          </Button>
          <Button onClick={() => setShowAssignDialog(true)} variant="outline" data-testid="bulk-assign-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Bulk Assign
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-[#003456]" data-testid="create-segment-btn">
            <Plus className="h-4 w-4 mr-2" /> New Segment
          </Button>
        </div>
      </div>

      {/* Segments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {segments.map(segment => (
          <div 
            key={segment.id} 
            className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${
              segment.is_predefined ? 'border-l-4 border-l-[#CE8946]' : ''
            }`}
            data-testid={`segment-card-${segment.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-[#003456]" />
                  <h3 className="font-semibold text-gray-900">{segment.name}</h3>
                  {segment.is_predefined && (
                    <span className="text-[10px] bg-[#CE8946] text-[#0a0203] px-1.5 py-0.5 rounded">
                      LIFESTYLE
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{segment.description}</p>
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-sm">
                    <Users className="h-4 w-4 inline mr-1 text-gray-400" />
                    <span className="font-medium">{segment.subscriber_count || 0}</span> subscribers
                  </span>
                </div>
              </div>
              {!segment.is_predefined && (
                <button
                  onClick={() => deleteSegment(segment.id)}
                  className="text-gray-400 hover:text-red-500 p-1"
                  data-testid={`delete-segment-${segment.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Segment Distribution */}
      {segmentStats && segmentStats.distribution && segmentStats.distribution.length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Segment Distribution</h3>
          <div className="space-y-3">
            {segmentStats.distribution.slice(0, 10).map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-32 text-sm text-gray-600 truncate">{item.segment_name}</div>
                <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#003456] rounded-full transition-all"
                    style={{ 
                      width: `${Math.min(100, (item.count / segmentStats.total_subscribers) * 100)}%` 
                    }}
                  />
                </div>
                <div className="w-16 text-sm text-right font-medium">{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Segment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Custom Segment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Segment Name</label>
              <Input
                value={newSegment.name}
                onChange={(e) => setNewSegment({ ...newSegment, name: e.target.value })}
                placeholder="e.g., VIP Customers, Early Adopters"
                data-testid="new-segment-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <Input
                value={newSegment.description}
                onChange={(e) => setNewSegment({ ...newSegment, description: e.target.value })}
                placeholder="Brief description of this segment"
                data-testid="new-segment-description"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={createSegment} className="bg-[#003456]" data-testid="save-segment-btn">
                Create Segment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Assign Subscribers to Segment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Segment Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700">Select Segment</label>
              <select
                className="w-full p-3 border rounded-lg mt-1"
                value={selectedSegment || ''}
                onChange={(e) => setSelectedSegment(e.target.value)}
                data-testid="assign-segment-select"
              >
                <option value="">Choose a segment...</option>
                {segments.map(seg => (
                  <option key={seg.id} value={seg.id}>{seg.name}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-10"
                placeholder="Search subscribers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Selection Info */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {filteredSubscribers.length} subscribers found
              </span>
              <span className="text-[#003456] font-medium">
                {selectedEmails.length} selected
              </span>
            </div>

            {/* Subscriber List */}
            <div className="border rounded-lg max-h-60 overflow-y-auto">
              {filteredSubscribers.slice(0, 100).map(sub => (
                <div 
                  key={sub.email}
                  className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                    selectedEmails.includes(sub.email) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => toggleEmail(sub.email)}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                    selectedEmails.includes(sub.email) 
                      ? 'bg-[#003456] border-[#003456]' 
                      : 'border-gray-300'
                  }`}>
                    {selectedEmails.includes(sub.email) && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{sub.email}</p>
                    {sub.name && <p className="text-xs text-gray-500">{sub.name}</p>}
                  </div>
                  <div className="flex gap-1">
                    {(sub.segments || []).map(segId => {
                      const seg = segments.find(s => s.id === segId);
                      return seg ? (
                        <span key={segId} className="text-[10px] bg-gray-200 px-1.5 py-0.5 rounded">
                          {seg.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowAssignDialog(false);
                setSelectedEmails([]);
                setSelectedSegment(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={assignSubscribers} 
                className="bg-[#003456]"
                disabled={!selectedSegment || selectedEmails.length === 0}
                data-testid="confirm-assign-btn"
              >
                Assign {selectedEmails.length} Subscribers
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Smart Suggestions Dialog */}
      <Dialog open={showSuggestionsDialog} onOpenChange={setShowSuggestionsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Smart Segment Suggestions
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                These suggestions are based on subscriber browsing behavior, including pages visited and time spent on content.
              </p>
            </div>

            {suggestions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No suggestions available.</p>
                <p className="text-sm mt-1">Subscribers need browsing history to generate suggestions.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{suggestions.length} suggestions found</span>
                  <Button 
                    size="sm" 
                    onClick={applyAllSuggestions}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Zap className="h-3 w-3 mr-1" /> Auto-Apply All (50%+ confidence)
                  </Button>
                </div>

                <div className="border rounded-lg max-h-60 overflow-y-auto divide-y">
                  {suggestions.map((suggestion, idx) => (
                    <div key={idx} className="p-3 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{suggestion.email}</p>
                          {suggestion.name && <p className="text-xs text-gray-500">{suggestion.name}</p>}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            suggestion.top_confidence >= 70 ? 'bg-green-100 text-green-700' :
                            suggestion.top_confidence >= 50 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {suggestion.top_confidence}% confident
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {suggestion.suggestions.map((seg, sidx) => (
                          <button
                            key={sidx}
                            onClick={() => applySmartSuggestion(suggestion.email, seg)}
                            className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors flex items-center gap-1"
                          >
                            <TrendingUp className="h-3 w-3" />
                            {seg}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowSuggestionsDialog(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SegmentManager;
